import React, { Fragment } from 'react';

import { HtmlSanitizer } from '@BICE_Vida/scp-components';

import { Button } from '@BICE_Vida/components';
import { availablePlanCard, comparePlansTexts } from '@/utils/generalTexts';
import { Text } from '../../designSystem';

import styles from './PlansComparisonTable.module.scss';

import { AvailablePlan, Badges } from '../../types/availablePlans';
import { UF } from '../../types/uf';
import { useCyberDates } from '../../customHooks/index';

import { extractUFfromBold } from '@/utils/comparePlans';
import { trackClick, clickActionsEvents } from '@/utils/dataLayer/home';

type PlansComparisonTableHeaderT = {
  availablePlan: AvailablePlan;
  UF: UF;
  setIndex: (index: number) => void;
  idx: number;
  openPlanDetailsModal: () => void;
  isLoading?: boolean;
};

const Badge = ({ label, icon, background, color }: Badges) => {
  return (
    <div
      className={styles.card_chip}
      style={{
        backgroundColor: `#${background}`,
        color: `#${color}`,
      }}
    >
      <i>{icon}</i>
      <HtmlSanitizer html={label} allowedTags={['b']} />
    </div>
  );
};

const PlansComparisonTableHeader = ({
  availablePlan,
  UF,
  setIndex,
  idx,
  openPlanDetailsModal,
  isLoading = false,
}: PlansComparisonTableHeaderT) => {
  const { isPromo, isCyberDayToday } = useCyberDates(new Date());

  return (
    <Fragment>
      {(isPromo || isCyberDayToday || availablePlan.badges) && (
        <div className={styles.chip_section}>
          {isPromo || isCyberDayToday
            ? availablePlanCard.cyberText.chips[
                availablePlan.base as 'standard' | 'premium' | 'elite'
              ].map((chip, i) => <Badge {...chip} key={`badge_${chip.label}_${i}`} />)
            : availablePlan.badges
                ?.filter((badge) => badge.show === true)
                .map((badge, i) => <Badge {...badge} key={`badge_${badge.label}_${i}`} />)}
        </div>
      )}

      <div className={styles.compare_plan_description_container}>
        <Text
          text={availablePlan.description}
          customClass={styles.compare_plan_header_description}
        />
        <Text
          text={comparePlansTexts.subtitle(
            availablePlan.coverage.health.value,
            extractUFfromBold(availablePlan.cardCoverages[3])
          )}
          customClass={styles.compare_plan_header_name}
        />
        <Text text={comparePlansTexts.from} customClass={styles.compare_plan_header_caption} />

        {isLoading ? (
          <div className={styles.skeleton_container}>
            <div className={`${styles.skeleton_line} ${styles['skeleton_line--price']}`} />
            <div className={`${styles.skeleton_line} ${styles['skeleton_line--perWorker']}`} />
            <div className={`${styles.skeleton_line} ${styles['skeleton_line--iva']}`} />
          </div>
        ) : (
          <>
            <span>
              <HtmlSanitizer
                className={styles.compare_plan_header_price}
                html={comparePlansTexts.price(availablePlan.price.uf, UF.value)}
                allowedTags={['span', 'strong']}
              />
            </span>
            <Text
              text={comparePlansTexts.perWorker(availablePlan.price.uf)}
              customClass={styles.compare_plan_header_caption}
            />
            <Text text={comparePlansTexts.iva} customClass={styles.compare_plan_header_caption} />
          </>
        )}
        <div className={styles.compare_plan_header_buttons_container}>
          <Button
            size="medium"
            onClick={() => {
              setIndex(idx);
              openPlanDetailsModal();
              trackClick({
                action: clickActionsEvents.comparisonTableBtn(availablePlan.description),
              });
            }}
            variant="text"
            className={styles.compare_plan_header_link}
            text="Ver detalles"
          />
        </div>
      </div>
    </Fragment>
  );
};

export default PlansComparisonTableHeader;
